import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface AdminStatistics {
  totalUsers: number;
  usersByTier: {
    free: number;
    standard: number;
    premium: number;
    ultimate: number;
  };
  totalPosts: number;
  totalPhotos: number;
  totalVideos: number;
}

export const useAdminStatistics = () => {
  return useQuery({
    queryKey: ['admin-statistics'],
    queryFn: async (): Promise<AdminStatistics> => {
      // Check admin access first
      const { data: adminCheck } = await supabase.rpc('is_admin', {
        user_uuid: (await supabase.auth.getUser()).data.user?.id
      });
      
      if (!adminCheck) {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Get all users data using admin function
      const { data: allUsers } = await supabase.rpc('admin_get_all_users');

      const usersByTier = {
        free: allUsers?.filter(u => u.subscription_tier === 'free').length || 0,
        standard: allUsers?.filter(u => u.subscription_tier === 'standard').length || 0,
        premium: allUsers?.filter(u => u.subscription_tier === 'premium').length || 0,
        ultimate: allUsers?.filter(u => u.subscription_tier === 'ultimate').length || 0,
      };

      // Get total posts count
      const { count: totalPosts } = await supabase
        .from('memory_posts')
        .select('*', { count: 'exact', head: true });

      // Get media files count (photos and videos)
      const { data: mediaFiles } = await supabase
        .from('media_files')
        .select('media_type');

      const photoTypes = ['image_original', 'image_edited', 'image_avatar', 'ai_generated_image'];
      const videoTypes = ['video_original', 'video_edited', 'ai_generated_video'];

      const totalPhotos = mediaFiles?.filter(m => photoTypes.includes(m.media_type)).length || 0;
      const totalVideos = mediaFiles?.filter(m => videoTypes.includes(m.media_type)).length || 0;

      return {
        totalUsers: allUsers?.length || 0,
        usersByTier,
        totalPosts: totalPosts || 0,
        totalPhotos,
        totalVideos,
      };
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};